import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, RotateCcw, Plus } from "lucide-react";
import { useApp } from "../context/AppContext";
import { SHADE_QUIZ, TX, shadeByCode } from "../data";
import { PRODUCTS } from "../mock";

const ShadeFinder = () => {
  const { t, lang, addToCart } = useApp();
  const tx = TX[lang];
  const navigate = useNavigate();
  const [step, setStep] = useState(-1); // -1 intro, 0..n questions, n = result
  const [answers, setAnswers] = useState([]);

  const total = SHADE_QUIZ.questions.length;
  const tintProduct = PRODUCTS.find((p) => p.id === "color-lip-tint");

  const choose = (value) => {
    const next = [...answers, value];
    setAnswers(next);
    setStep((s) => s + 1);
  };

  const restart = () => { setAnswers([]); setStep(-1); };

  // Tally most frequent shade code
  const result = (() => {
    if (answers.length < total) return null;
    const counts = {};
    answers.forEach((a) => (counts[a] = (counts[a] || 0) + 1));
    const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
    return shadeByCode(best);
  })();

  const addRecommended = () => {
    if (tintProduct && result) addToCart(tintProduct, result, 1);
  };

  return (
    <main className="max-w-[820px] mx-auto px-6 py-16 md:py-24 min-h-[70vh]">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-[#B4715C]">
          <Sparkles className="h-4 w-4" />
          <span className="text-[11px] tracking-label uppercase">{tx.shadeKicker}</span>
        </div>
        <h1 className="mt-4 font-serif font-light text-5xl md:text-6xl text-[#3A2C28] leading-[1.02]">{tx.shadeTitle}</h1>
      </div>

      <div className="mt-12 bg-white rounded-[2rem] border border-[#EEE0D7] p-8 md:p-12 shadow-[0_30px_70px_-40px_rgba(150,100,80,0.35)]">
        {step === -1 && (
          <div className="text-center">
            <p className="text-[16px] text-[#6E5A52] leading-relaxed max-w-md mx-auto">{tx.shadeIntro}</p>
            <button onClick={() => setStep(0)} className="mt-8 inline-flex items-center gap-2 h-12 px-9 rounded-full bg-[#3A2C28] text-white text-[11px] tracking-label uppercase hover:bg-[#B4715C]">
              {tx.shadeStart} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {step >= 0 && step < total && (
          <div>
            <div className="flex items-center gap-3 mb-8">
              <span className="text-[11px ]tracking-label uppercase text-[#A08678]">{tx.question} {step + 1} {tx.of} {total}</span>
              <div className="flex-1 h-1 rounded-full bg-[#F1E2D9] overflow-hidden">
                <div className="h-full bg-[#B4715C] transition-all duration-500" style={{ width: `${((step + 1) / total) * 100}%` }} />
              </div>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#3A2C28] leading-tight">{SHADE_QUIZ.questions[step].q[lang]}</h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {SHADE_QUIZ.questions[step].options.map((opt, i) => (
                <button key={i} onClick={() => choose(opt.value)}
                  className="group flex items-center justify-between px-6 h-16 rounded-2xl border border-[#EADBD1] bg-[#FBF5F0] text-left hover:border-[#B4715C] hover:bg-white transition-all">
                  <span className="text-[16px] text-[#3A2C28]">{opt[lang]}</span>
                  <span className="h-7 w-7 rounded-full" style={{ backgroundColor: shadeByCode(opt.value).hex }} />
                </button>
              ))}
            </div>
          </div>
        )}

        {step >= total && result && (
          <div className="text-center">
            <p className="text-[11px] tracking-label uppercase text-[#A08678]">{tx.shadeResult}</p>
            <div className="mt-6 mx-auto h-28 w-28 rounded-full shadow-lg" style={{ backgroundColor: result.hex, boxShadow: "inset 0 -6px 12px rgba(0,0,0,0.15), inset 0 6px 10px rgba(255,255,255,0.5)" }} />
            <h2 className="mt-6 font-serif text-4xl text-[#3A2C28]">Tint {result.code} · {result.name}</h2>
            <p className="mt-3 text-[15px] text-[#6E5A52] max-w-sm mx-auto">{tintProduct?.short[lang]}</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button onClick={addRecommended} className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-[#B4715C] text-white text-[11px] tracking-label uppercase hover:bg-[#9E5F4B]">
                <Plus className="h-4 w-4" /> {tx.shadeAdd} {result.code}
              </button>
              <button onClick={() => navigate("/product/color-lip-tint")} className="h-12 px-8 rounded-full border border-[#D6B7A9] text-[#5A4740] text-[11px] tracking-label uppercase hover:border-[#B4715C] hover:text-[#B4715C]">
                {tx.viewDetails}
              </button>
              <button onClick={restart} className="inline-flex items-center gap-2 text-[11px] tracking-label uppercase text-[#8A756C] hover:text-[#B4715C]">
                <RotateCcw className="h-4 w-4" /> {tx.shadeRetry}
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default ShadeFinder;
